import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import DraftsIcon from '@mui/icons-material/Drafts';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import Spacings from '~/modules/ui-kit/spacings';
import commonMessages from '~/containers/App/messages';
import { useMarkNewsEntryAsRead } from '~/hooks/api/useMarkNewsEntryAsRead';
import { TiptapEditor, useTiptap } from '~/containers/NewsEntryForm/Tiptap';
import Download from '@mui/icons-material/Download';
import InsertDriveFile from '@mui/icons-material/InsertDriveFile';
import styles from './styles.module.css';
import messages from './messages';

type Attachment = {
  id: number;
  fileName: string;
  contentType: string;
  blobId: string;
};

type NewsCardEntryProps = {
  newsEntry: {
    id: number;
    title: string;
    date: string;
    content: string;
    revision: number;
    isRead: 'Unread' | 'ReadButChanged' | 'Read';
    attachments: Attachment[];
  };
};

const NewsCardEntry = (props: NewsCardEntryProps) => {
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const markAsReadMutation = useMarkNewsEntryAsRead({
    onError() {
      showSnackbar({
        message: intl.formatMessage(messages.markAsReadFailed),
      });
    },
  });
  const editor = useTiptap({
    content: props.newsEntry.content,
    editable: false,
  });
  return (
    <div className={styles.entry} data-testid="news-entry">
      <Spacings.Inset>
        <Spacings.Stack>
          <Spacings.Inline>
            <Typography
              className={styles.title}
              sx={{
                fontWeight: 500,
              }}
            >
              {props.newsEntry.title}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {moment(props.newsEntry.date).format('L')}
            </Typography>
          </Spacings.Inline>
          <div>
            <TiptapEditor editor={editor} />
          </div>
        </Spacings.Stack>
      </Spacings.Inset>
      {props.newsEntry.attachments.length > 0 ? (
        <List>
          {props.newsEntry.attachments.map((attachment) => (
            <ListItem dense disableGutters key={attachment.id}>
              <ListItemButton
                target="_blank"
                href={`/api/blobs/${attachment.blobId}`}
              >
                <ListItemIcon>
                  <InsertDriveFile />
                </ListItemIcon>
                <ListItemText primary={attachment.fileName} />
                <ListItemSecondaryAction>
                  <Download />
                </ListItemSecondaryAction>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      ) : null}
      {props.newsEntry.isRead !== 'Read' ? (
        <Spacings.Inset className={styles['mark-as-read']}>
          <Button
            size="small"
            onClick={() => markAsReadMutation.mutate(props.newsEntry)}
            disabled={markAsReadMutation.isPending}
            startIcon={<DraftsIcon />}
          >
            {markAsReadMutation.isPending ? (
              <FormattedMessage {...commonMessages.loading} />
            ) : (
              <FormattedMessage {...messages.markAsRead} />
            )}
          </Button>
        </Spacings.Inset>
      ) : null}
    </div>
  );
};

export default NewsCardEntry;
